import { API_CONFIG } from '../config/api';
import type { AuthUser, LoginCredentials, LoginResponse, MeResponse } from '../types/auth';
import { ApiValidationError } from '../types/auth';

const AUTH_STORAGE_KEYS = [
  'token',
  'user',
  'user_name',
  'user_role',
  'company_id',
  'company_currency',
] as const;

export const storeAuthSession = (token: string, user: AuthUser) => {
  localStorage.setItem('token', token);
  localStorage.setItem('user', JSON.stringify(user));
  localStorage.setItem('user_name', user.name);
  localStorage.setItem('user_role', user.role.toLowerCase());

  if (user.company?.id) {
    localStorage.setItem('company_id', String(user.company.id));
  }

  if (user.company?.currency) {
    localStorage.setItem('company_currency', user.company.currency);
  }
};

export const clearAuthSession = () => {
  AUTH_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));
};

export const isAuthenticated = () => {
  const token = localStorage.getItem('token');
  const user = localStorage.getItem('user');
  return Boolean(token && user);
};

/** Returns the signed-in user's id or throws if the session user is missing/invalid. */
export const getCurrentUserId = (): number => {
  const raw = localStorage.getItem('user');
  if (!raw) {
    throw new Error('User session expired. Please sign in again.');
  }

  let user: { id?: unknown };
  try {
    user = JSON.parse(raw);
  } catch {
    throw new Error('User session expired. Please sign in again.');
  }

  const id = Number(user.id);
  if (!Number.isFinite(id) || id <= 0) {
    throw new Error('User session expired. Please sign in again.');
  }

  return id;
};

const parseErrorResponse = async (response: Response) => {
  const errorData = await response.json().catch(() => ({}));
  const message = errorData.message || `Request failed (${response.status})`;

  if (response.status === 422 && errorData.errors) {
    throw new ApiValidationError(message, errorData.errors);
  }

  throw new Error(message);
};

export const login = async (credentials: LoginCredentials): Promise<LoginResponse> => {
  const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.AUTH.LOGIN}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify(credentials),
  });

  if (!response.ok) {
    await parseErrorResponse(response);
  }

  return response.json();
};

export const getMe = async (): Promise<AuthUser> => {
  const token = localStorage.getItem('token');

  if (!token) {
    throw new Error('No authentication token');
  }

  const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.AUTH.ME}`, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    await parseErrorResponse(response);
  }

  const result: MeResponse = await response.json();
  return result.data;
};

export const restoreSession = async (): Promise<AuthUser | null> => {
  if (!localStorage.getItem('token')) {
    return null;
  }

  try {
    const user = await getMe();
    storeAuthSession(localStorage.getItem('token')!, user);
    return user;
  } catch {
    clearAuthSession();
    return null;
  }
};

export const logout = async () => {
  try {
    const token = localStorage.getItem('token');

    if (token) {
      await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.AUTH.LOGOUT}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json',
        },
      });
    }
  } catch (error) {
    console.error('Logout API error:', error);
  } finally {
    clearAuthSession();
    window.location.href = '/signin';
  }
};

export type { LoginCredentials, AuthUser, LoginResponse } from '../types/auth';
export { ApiValidationError } from '../types/auth';
